<template>
  <!-- MYAC-1522 (onboarding pass, decisions H and E) — what someone with nothing
       to look at is offered, on the dashboard and on an empty QMS / product /
       watch list: ONE true action from the role and the plan (utils/firstStep.ts).
       It replaces the 2025 first-run card, which promised a QMS and a technical
       documentation to plans that have neither, and the lists' empty grid
       (« Aucune option disponible »). The frame is the watch module's
       `EmptyStateBlock`, so every empty state has the same shape. The chat with
       no project says the same thing, `bare` inside its own card (MYAC-1522). -->
  <div v-if="ready" class="empty-projects" data-testid="empty-projects-state" :data-step="step.kind">
    <!-- The admin's read of the company's projects failed: an error, never a create
         that could duplicate what exists (client-data-contract §4). -->
    <Message v-if="readFailed" severity="error" :closable="false" data-testid="first-step-read-failed">
      {{ t('first_step.read_failed') }}
    </Message>
    <EmptyStateBlock
      v-else-if="step.kind === 'join'"
      icon="pi pi-sign-in"
      :title="t('first_step.join.title')"
      :body="t('first_step.join.body', { company: companyName, names: outsideNames }, outsideCount)"
      highlighted
      :bare="bare"
    >
      <LinkButton :to="{ name: 'settings-projects' }" icon="pi pi-sign-in" :label="t('first_step.join.action')"
        data-testid="first-step-join" />
    </EmptyStateBlock>

    <EmptyStateBlock
      v-else-if="step.kind === 'create'"
      :icon="ICONS[step.type]"
      :title="t(`first_step.create.${step.type}.title`)"
      :body="t(`first_step.create.${step.type}.body`)"
      highlighted
      :bare="bare"
    >
      <Button :icon="ICONS[step.type]" :label="t(`first_step.create.${step.type}.action`)"
        data-testid="first-step-create" @click="dialogVisible = true" />
      <template v-if="step.alsoUpgrade">
        <p class="m-0 pt-2 text-sm text-muted-color" data-testid="first-step-also-upgrade">{{ t('first_step.upgrade.also') }}</p>
        <UpgradePlanButton />
      </template>
    </EmptyStateBlock>

    <EmptyStateBlock
      v-else-if="step.kind === 'upgrade'"
      icon="pi pi-arrow-circle-up"
      :title="t(`first_step.upgrade.${step.type ?? 'none'}.title`)"
      :body="t(`first_step.upgrade.${step.type ?? 'none'}.body`)"
      highlighted
      :bare="bare"
    >
      <UpgradePlanButton />
    </EmptyStateBlock>

    <EmptyStateBlock
      v-else
      icon="pi pi-users"
      :title="t(`first_step.ask_admin.title.${scope ?? 'any'}`)"
      :body="askAdminBody"
      :bare="bare"
    />

    <ProjectCreateDialog v-if="step.kind === 'create'" v-model:visible="dialogVisible"
      :initialType="step.type" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef } from 'vue'
import { useI18n } from 'vue-i18n'
import Button from 'primevue/button'
import Message from 'primevue/message'
import LinkButton from '@/components/shared/LinkButton.vue'
import { ProjectCreationRequestType } from '@/models/Billing'
import { useFirstStep } from '@/composables/useFirstStep'
import EmptyStateBlock from '@/components/regulatory-monitoring/EmptyStateBlock.vue'
import ProjectCreateDialog from '@/components/projects/ProjectCreateDialog.vue'
import UpgradePlanButton from '@/components/shared/UpgradePlanButton.vue'

const props = defineProps<{
  /** The list the screen is about; null on the dashboard (and in the chat). */
  scope: ProjectCreationRequestType | null
  /** Without a card of its own: the host is the frame (the chat panel). */
  bare?: boolean
}>()

const ICONS: Record<ProjectCreationRequestType, string> = {
  [ProjectCreationRequestType.QMS]: 'pi pi-sitemap',
  [ProjectCreationRequestType.TD]: 'pi pi-box',
  [ProjectCreationRequestType.REGULATORY_MONITORING]: 'pi pi-eye',
}

const { t } = useI18n()
const { step, ready, readFailed, companyName, outsideNames, outsideCount, adminNames } = useFirstStep(toRef(props, 'scope'))
const dialogVisible = ref(false)

const askAdminBody = computed(() => adminNames.value
  ? t('first_step.ask_admin.body', { company: companyName.value, admins: adminNames.value })
  : t('first_step.ask_admin.body_no_names', { company: companyName.value }))
</script>

<style scoped>
.empty-projects { max-width: 26rem; }
</style>
